import { distribution } from 'repositories/distribution';
import {
  convertToApplication,
  convertToCommentList,
  convertToDistribution,
} from 'models/Distribution/transfer';
import { Application } from 'models/Application/type';
import { DeviceDomain } from 'models/Device';
import { Device } from 'models/Device/type';
import { NotifyDomain } from 'models/Notify';
import { Distribution, CommentList } from 'models/Distribution/type';
import { ApplicationError } from 'models/Error';
import { Failure, Result, Success } from 'types';
import { APIDomain } from 'models/API';

export class DistributionDomain {
  private readonly accessKey: string;
  private readonly deviceDomain: DeviceDomain;
  private readonly notifyDomain: NotifyDomain;

  constructor(accessKey: string, device?: Device) {
    this.accessKey = accessKey;
    this.deviceDomain = new DeviceDomain(device);
    this.notifyDomain = NotifyDomain.getInstance();
  }

  async install(
    installSessionToken: string,
    overwrite: boolean
  ): Promise<Result<undefined, ApplicationError>> {
    if (!this.deviceDomain.isInstalledConfigurationProfile()) {
      return new Failure(
        new ApplicationError('required install configuration profile')
      );
    }

    try {
      const itmsBaseUrl = 'itms-services://?action=download-manifest';
      const baseUrl = `${APIDomain.origin()}/api/iphone/install_sessions/${installSessionToken}/distribution_plist`;
      const url = overwrite ? `${baseUrl}?overwrite_install=true` : baseUrl;
      location.href = `${itmsBaseUrl}&url=${encodeURIComponent(url)}`;

      await this.notifyDomain.subscribeDownloadIpa(
        this.deviceDomain.value?.uid
      );

      return new Success(undefined);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async installSessionToken(): Promise<Result<string, ApplicationError>> {
    try {
      const response = await distribution.installSession(this.accessKey);
      return new Success(response.results.session_token);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async detail(): Promise<
    Result<[Application, Distribution], ApplicationError>
  > {
    if (!this.deviceDomain.isInstalledConfigurationProfile()) {
      return new Failure(
        new ApplicationError('required install configuration profile')
      );
    }

    try {
      const response = await distribution.show(this.accessKey);
      return new Success([
        convertToApplication(response),
        convertToDistribution(response),
      ]);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async join(
    passCodeKey: string,
    displayName: string
  ): Promise<Result<undefined, ApplicationError>> {
    if (!this.deviceDomain.isInstalledConfigurationProfile()) {
      return new Failure(
        new ApplicationError('required install configuration profile')
      );
    }

    try {
      await distribution.join(this.accessKey, passCodeKey, displayName);
      return new Success(undefined);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async leave(): Promise<Result<undefined, ApplicationError>> {
    if (!this.deviceDomain.isInstalledConfigurationProfile()) {
      return new Failure(
        new ApplicationError('required install configuration profile')
      );
    }

    try {
      await distribution.leave(this.accessKey);
      return new Success(undefined);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async updateDisplayName(
    displayName: string
  ): Promise<Result<string, ApplicationError>> {
    if (!this.deviceDomain.isInstalledConfigurationProfile()) {
      return new Failure(
        new ApplicationError('required install configuration profile')
      );
    }

    try {
      await distribution.updateDisplayName(this.accessKey, displayName);
      return new Success(displayName);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async commentList(
    page?: number
  ): Promise<Result<CommentList, ApplicationError>> {
    if (!this.deviceDomain.isInstalledConfigurationProfile()) {
      return new Failure(
        new ApplicationError('required install configuration profile')
      );
    }

    try {
      const response = await distribution.commentList(this.accessKey, page);
      return new Success(convertToCommentList(response));
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async postComment(
    message: string,
    replyTo?: number
  ): Promise<Result<undefined, ApplicationError>> {
    if (!this.deviceDomain.isInstalledConfigurationProfile()) {
      return new Failure(
        new ApplicationError('required install configuration profile')
      );
    }

    try {
      await distribution.postComment(this.accessKey, message, replyTo);
      return new Success(undefined);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async deleteComment(
    id: number
  ): Promise<Result<undefined, ApplicationError>> {
    if (!this.deviceDomain.isInstalledConfigurationProfile()) {
      return new Failure(
        new ApplicationError('required install configuration profile')
      );
    }

    try {
      await distribution.deleteComment(this.accessKey, id);
      return new Success(undefined);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async setNotification(
    enable: boolean
  ): Promise<Result<undefined, ApplicationError>> {
    if (!this.deviceDomain.isInstalledConfigurationProfile()) {
      return new Failure(
        new ApplicationError('required install configuration profile')
      );
    }

    try {
      await distribution.setNotification(this.accessKey, enable);
      return new Success(undefined);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }
}
